import { useQuery } from '@tanstack/react-query';
import type { SwiperBreakpoints } from '@uefadigital/panenka';
import { PkCarousel } from '@uefadigital/panenka-react';
import objectHash from 'object-hash';
import { memo, useMemo } from 'react';

import Card from './Card';
import type { FootballRailOptions, Slot } from './types';
import { SlotType } from './types';

import { useLivescore } from '@hooks/useLivescore';
import { getLibraryContentQuery } from '@queries/common';
import { IS_TEST } from '@utilities/browser';
import { uniq } from '@utilities/collection-utils';

const carouselBreakpoints: SwiperBreakpoints = {
  0: {
    slidesPerView: 'auto',
    spaceBetween: 16,
    freeMode: {
      sticky: true,
    },
  },
} as const;

const placeholderSlots = Array.from(
  { length: 5 },
  (_, i) =>
    ({
      id: i.toString(),
      type: SlotType.PLACEHOLDER,
    }) as Slot,
);

const FootballRail = ({ options }: { options: FootballRailOptions }) => {
  const { data: slots } = useQuery({
    ...getLibraryContentQuery<Slot[]>(options.libraryUrl),
    placeholderData: placeholderSlots,
    refetchInterval: !IS_TEST && options.interval,
  });

  const competitionIds = useMemo(() => uniq(slots.map(({ competition }) => competition?.id).filter(Boolean)), [slots]);
  const liveMatches = useLivescore(competitionIds);

  return (
    <PkCarousel
      className='js-tracking-container'
      key={objectHash(slots.map(({ id }) => id))}
      navigationPosition='inside'
      resizeObserver
      breakpoints={carouselBreakpoints}
    >
      {slots.map((slot) => (
        <div key={slot.id} className='pk-w--auto pk-h--auto'>
          <Card slot={slot} options={options} liveMatches={liveMatches} />
        </div>
      ))}
    </PkCarousel>
  );
};

export default memo(FootballRail);
